import React, {useEffect} from "react";
import {Box, Grid} from "@mui/material";
import {useRecoilValue} from "recoil";
import {financialInfoState, representativeInfoState} from "../../../search-dms/state";
import {useForm, FormProvider} from "react-hook-form";
import {useTranslation} from "react-i18next";
import FormTextField from "../../../common/form-fields-wrappers/FormTextField";
import {ALL_COUNTRIES_PREFIX, CountryType} from "../../../data";
import {FormAutocomplete} from "../../../common/form-fields-wrappers";

const RepresentativeInfo = () => {
    const {t} = useTranslation(["network"]);
    const countriesPrefix: CountryType[] = ALL_COUNTRIES_PREFIX;
    const representativeInfo = useRecoilValue(representativeInfoState);
    const financialInfo = useRecoilValue(financialInfoState);
    const representativeInfoForm = useForm({
        defaultValues: {
            id: representativeInfo?.id,
            representativeIdentificationCode: representativeInfo?.representativeIdentificationCode ?? "",
            companyName: representativeInfo?.companyName ?? "",
            nameAndSurnameOfRepresentative: representativeInfo?.nameAndSurnameOfRepresentative ?? "",
            emailOfRepresentative: representativeInfo?.emailOfRepresentative ?? "",
            prefixCellPhoneNumberOfRepresentative: representativeInfo?.prefixCellPhoneNumberOfRepresentative ?? "",
            cellPhoneNumberOfRepresentative: representativeInfo?.cellPhoneNumberOfRepresentative ?? "",
            vatNumber: representativeInfo?.vatNumber ?? "",
            siren: representativeInfo?.siren ?? "",
            siret: representativeInfo?.siret ?? "",
            taxCode: representativeInfo?.taxCode ?? "",
            sdiCode: representativeInfo?.sdiCode ?? "",
            nameAndSurnameOfTheContactPerson: representativeInfo?.nameAndSurnameOfTheContactPerson ?? "",
            emailOfTheContactPerson: representativeInfo?.emailOfTheContactPerson ?? "",
            prefixCellPhoneNumberOfTheContactPerson: representativeInfo?.prefixCellPhoneNumberOfTheContactPerson ?? "",
            cellPhoneNumberOfTheContactPerson: representativeInfo?.cellPhoneNumberOfTheContactPerson ?? "",
            iban: representativeInfo?.iban ?? "",
            address: representativeInfo?.address ?? "",
            streetNumber: representativeInfo?.streetNumber ?? "",
            postalCode: representativeInfo?.postalCode ?? "",
            city: representativeInfo?.city ?? "",
            province: representativeInfo?.province ?? "",
            prefixHoCellPhoneNumber: representativeInfo?.prefixHoCellPhoneNumber ?? "",
            hoCellPhoneNumber: representativeInfo?.hoCellPhoneNumber ?? "",
            email: representativeInfo?.email ?? "",
            pec: representativeInfo?.pec ?? "",
            incomeCode: financialInfo?.incomeCode ?? ""
        }
    });

    useEffect(() => {
        if (!!representativeInfo) {
            representativeInfoForm.setValue("id", representativeInfo?.id);
            representativeInfoForm.setValue("representativeIdentificationCode", representativeInfo?.representativeIdentificationCode);
            representativeInfoForm.setValue("companyName", representativeInfo?.companyName);
            representativeInfoForm.setValue("nameAndSurnameOfRepresentative", representativeInfo?.nameAndSurnameOfRepresentative);
            representativeInfoForm.setValue("emailOfRepresentative", representativeInfo?.emailOfRepresentative);
            representativeInfoForm.setValue("prefixCellPhoneNumberOfRepresentative", representativeInfo?.prefixCellPhoneNumberOfRepresentative);
            representativeInfoForm.setValue("cellPhoneNumberOfRepresentative", representativeInfo?.cellPhoneNumberOfRepresentative);
            representativeInfoForm.setValue("vatNumber", representativeInfo?.vatNumber);
            representativeInfoForm.setValue("siren", representativeInfo?.siren);
            representativeInfoForm.setValue("siret", representativeInfo?.siret);
            representativeInfoForm.setValue("taxCode", representativeInfo?.taxCode);
            representativeInfoForm.setValue("sdiCode", representativeInfo?.sdiCode);
            representativeInfoForm.setValue("nameAndSurnameOfTheContactPerson", representativeInfo?.nameAndSurnameOfTheContactPerson);
            representativeInfoForm.setValue("emailOfTheContactPerson", representativeInfo?.emailOfTheContactPerson);
            representativeInfoForm.setValue("prefixCellPhoneNumberOfTheContactPerson", representativeInfo?.prefixCellPhoneNumberOfTheContactPerson);
            representativeInfoForm.setValue("cellPhoneNumberOfTheContactPerson", representativeInfo?.cellPhoneNumberOfTheContactPerson);
            representativeInfoForm.setValue("iban", representativeInfo?.iban);
            representativeInfoForm.setValue("address", representativeInfo?.address);
            representativeInfoForm.setValue("streetNumber", representativeInfo?.streetNumber);
            representativeInfoForm.setValue("postalCode", representativeInfo?.postalCode);
            representativeInfoForm.setValue("city", representativeInfo?.city);
            representativeInfoForm.setValue("province", representativeInfo?.province);
            representativeInfoForm.setValue("prefixHoCellPhoneNumber", representativeInfo?.prefixHoCellPhoneNumber);
            representativeInfoForm.setValue("hoCellPhoneNumber", representativeInfo?.hoCellPhoneNumber);
            representativeInfoForm.setValue("email", representativeInfo?.email);
            representativeInfoForm.setValue("pec", representativeInfo?.pec);
        }
    }, [representativeInfo])

    useEffect(() => {
        if (!!financialInfo?.incomeCode) {
            representativeInfoForm.setValue("incomeCode", financialInfo?.incomeCode);
        }
    },[financialInfo])

    return (
        <>
            <FormProvider {...representativeInfoForm}>
                <Grid container direction="row" alignItems="start">
                    <Grid container item direction="row" xs={12} columnSpacing={2}>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="representativeIdentificationCode"
                                label={t('dmsDetail.representativeInfo.form.label.representativeIdentificationCode')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="companyName"
                                label={t('dmsDetail.representativeInfo.form.label.companyName')}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="nameAndSurnameOfRepresentative"
                                label={t('dmsDetail.representativeInfo.form.label.nameAndSurnameOfRepresentative')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="emailOfRepresentative"
                                label={t('dmsDetail.representativeInfo.form.label.emailOfRepresentative')}
                            />
                        </Grid>
                        <Grid item container xs={3} columnSpacing={2}>
                            <Grid item xs={6}>
                                <FormAutocomplete
                                    disabled
                                    control={representativeInfoForm.control}
                                    name="prefixCellPhoneNumberOfRepresentative"
                                    label={t('dmsDetail.representativeInfo.form.label.prefix')}
                                    options={countriesPrefix}
                                    getOptionLabel={(option: any) => option?.code + " +" + option?.description}
                                    isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                    renderOption={(props, option) => (
                                        <Box
                                            component="li"
                                            {...props}
                                        >
                                            {option?.code} + {option?.description}
                                        </Box>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormTextField
                                    disabled
                                    control={representativeInfoForm.control}
                                    name="cellPhoneNumberOfRepresentative"
                                    label={t('dmsDetail.representativeInfo.form.label.cellPhoneNumberOfRepresentative')}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="vatNumber"
                                label={t('dmsDetail.representativeInfo.form.label.vatNumber')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="siren"
                                label={t('dmsDetail.representativeInfo.form.label.siren')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="siret"
                                label={t('dmsDetail.representativeInfo.form.label.siret')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="taxCode"
                                label={t('dmsDetail.representativeInfo.form.label.taxCode')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="iban"
                                label={t('dmsDetail.representativeInfo.form.label.iban')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="sdiCode"
                                label={t('dmsDetail.representativeInfo.form.label.sdiCode')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="nameAndSurnameOfTheContactPerson"
                                label={t('dmsDetail.representativeInfo.form.label.nameAndSurnameOfTheContactPerson')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="emailOfTheContactPerson"
                                label={t('dmsDetail.representativeInfo.form.label.emailOfTheContactPerson')}
                            />
                        </Grid>
                        <Grid item container xs={3} columnSpacing={2}>
                            <Grid item xs={6}>
                                <FormAutocomplete
                                    disabled
                                    control={representativeInfoForm.control}
                                    name="prefixCellPhoneNumberOfTheContactPerson"
                                    label={t('dmsDetail.representativeInfo.form.label.prefixOfTheContactPerson')}
                                    options={countriesPrefix}
                                    getOptionLabel={(option: any) => option?.code + " +" + option?.description}
                                    isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                    renderOption={(props, option) => (
                                        <Box
                                            component="li"
                                            {...props}
                                        >
                                            {option?.code} + {option?.description}
                                        </Box>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormTextField
                                    disabled
                                    control={representativeInfoForm.control}
                                    name="cellPhoneNumberOfTheContactPerson"
                                    label={t('dmsDetail.representativeInfo.form.label.cellPhoneNumberOfTheContactPerson')}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="address"
                                label={t('dmsDetail.representativeInfo.form.label.address')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="streetNumber"
                                label={t('dmsDetail.representativeInfo.form.label.streetNumber')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="postalCode"
                                label={t('dmsDetail.representativeInfo.form.label.postalCode')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="city"
                                label={t('dmsDetail.representativeInfo.form.label.city')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="province"
                                label={t('dmsDetail.representativeInfo.form.label.province')}
                            />
                        </Grid>
                        <Grid item container xs={3} columnSpacing={2}>
                            <Grid item xs={6}>
                                <FormAutocomplete
                                    disabled
                                    control={representativeInfoForm.control}
                                    name="prefixHoCellPhoneNumber"
                                    label={t('dmsDetail.representativeInfo.form.label.prefixHoCellPhoneNumber')}
                                    options={countriesPrefix}
                                    getOptionLabel={(option: any) => option?.code + " +" + option?.description}
                                    isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                    renderOption={(props, option) => (
                                        <Box
                                            component="li"
                                            {...props}
                                        >
                                            {option?.code} +{option?.description}
                                        </Box>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormTextField
                                    disabled
                                    control={representativeInfoForm.control}
                                    name="hoCellPhoneNumber"
                                    label={t('dmsDetail.representativeInfo.form.label.hoCellPhoneNumber')}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="pec"
                                label={t('dmsDetail.representativeInfo.form.label.pec')}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <FormTextField
                                disabled
                                control={representativeInfoForm.control}
                                name="incomeCode"
                                label={t("dmsDetail.representativeInfo.form.label.incomeCode")}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </FormProvider>
        </>
    )
}

export default RepresentativeInfo;